import { useState, useRef } from "react";
import SamaritanFetch from "../../../api/httpClient";
import { dedupeMembersByPreferredMembership } from "util/membershipHelper";

// Type for the search API response
export interface SamaritanMemberSmallTemplate {
    id: number;
    member_id: number;
    name: string;
    date_of_birth: string;
    pii_member_id: string | null;
    pii_first_name: string;
    pii_last_name: string;
    beacon_id: string;
    email: string | null;
    phone: string | null;
    profile_stage: string;
    photo: string;
    notification_photo: string;
    mobile_photo: string;
}

export function useAdminMemberSearch() {
    const [query, setQueryState] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [results, setResults] = useState<SamaritanMemberSmallTemplate[]>([]);
    
    // Refs for debouncing and cancellation
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const searchMembers = async (searchQuery: string, signal?: AbortSignal): Promise<SamaritanMemberSmallTemplate[]> => {
        const params = new URLSearchParams({
            template: 'small',
            'samaritan_member[generic_search]': searchQuery
        });

        const response = await SamaritanFetch.v3.get(
            `/samaritan_members/search?${params.toString()}`,
            {
                signal,
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );

        if (!response.ok) {
            throw new Error('Failed to search members');
        }

        const data = await response.json();
        const members: SamaritanMemberSmallTemplate[] = data.samaritan_members || [];

        return dedupeMembersByPreferredMembership(members);
    };

    const handleSearch = async (q: string) => {
        // Cancel previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        // Create new AbortController
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        
        setIsLoading(true);
        try {
            const res = await searchMembers(q, abortController.signal);
            setResults(res);
        } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') {
                console.log("Search aborted");
                return; // Don't update state for aborted requests
            }
            if (err instanceof Error && err.message === 'Failed to fetch') {
                console.log("Search request was aborted");
                return;
            }
            console.error("Search failed:", err);
            setResults([]);
        } finally {
            setIsLoading(false);
        }
    };

    const setQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newQuery = e.target.value;
        setQueryState(newQuery);
        
        // Clear previous debounce timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        
        if (newQuery.trim() === "") {
            // Cancel any ongoing request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            setResults([]);
            setIsLoading(false);
        } else {
            // Show loading immediately when user starts typing
            setIsLoading(true);
            
            // Set new debounce timeout
            debounceTimeoutRef.current = setTimeout(() => {
                handleSearch(newQuery);
            }, 300); // 300ms debounce delay
        }
    };

    return {
        query,
        setQuery,
        results,
        isLoading
    };
}
